// ScreeningLayout.jsx
// Shared shell: card + header + progress + footer + sidebar
// Used by PCOSRiskScreening, MenopauseRiskScreening, MentalWellnessScreening
import React, { useState } from 'react';
import styles from './screening.module.css';
import { useNavigate } from 'react-router-dom';
import { BtnBack, BtnContinue } from '../Reusablecomponents/Button/Reusableutton';

// ── Icons ────────────────────────────────────────────────────
export const ChevronRight = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
export const ChevronLeft = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
export const Check = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
export const EditIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ── Logo ─────────────────────────────────────────────────────
// export const Logo = () => (
//   <div className={styles.logoFallback}>
//     <div className={styles.logoFallbackIcon}>🌸</div>
//     <div className={styles.logoFallbackText}>Fem<span>Care</span></div>
//   </div>
// );

export const Logo = () => {
  const navigate = useNavigate();
  return (
    <div className='flex items-center gap-2' onClick={() => navigate('/')} style={{ cursor: "pointer" }}>
      <img
        src="/Home/FemCareLogo.svg"
        alt="femcare"
        className='h-8 md:h-8 w-auto'
      />
    </div>
  );
}

// ── App Shell (full-page wrapper with gradient bg + sidebar) ─
export function AppShell({ accent = 'accentPurple', children }) {
  return (
    <div className={`${styles.root} ${styles[accent]}`}>
      <div className={styles.appShell}>{children}</div>
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────
export function Card({ children }) {
  return <div
    className={styles.card}
  >{children}</div>;
}

// ── Card Header ──────────────────────────────────────────────
export function CardHeader({ stepLabel, stepNum, totalSteps, onExit, showBack, onBack }) {
  return (
    <div className={styles.cardHeader}>
      <Logo />
      <div className={styles.headerRight}>
        {/* {stepLabel && (
          <span className={styles.stepPill}>{stepLabel}</span>
        )} */}
        {onExit && (
          <button className={styles.headerExitBtn} onClick={onExit}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Exit
          </button>
        )}
      </div>
    </div>
  );
}

// ── Progress Bar ─────────────────────────────────────────────
export function ProgressBar({ step, total, label }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles.progressMeta}>
        <span className={styles.progressStep}>{label ? label : `Step ${step} of ${total}`}</span>
        <span className={styles.progressPct}>{pct}%</span>
      </div>
    </div>
  );
}

// ── Card Body ────────────────────────────────────────────────
export function CardBody({ children }) {
  return <div className={styles.cardBody}>{children}</div>;
}

// ── Card Footer / Nav ────────────────────────────────────────
export function CardFooter({ onBack, onNext, nextLabel = 'Continue', disabled = false, showBack = true }) {
  return (
    <div className={styles.cardFooter}>
      {/* {showBack
        ? <button className={styles.footerBack} onClick={onBack}><ChevronLeft /> Back</button>
        : <span />
      } */}
      <BtnBack onClick={onBack} />
      {/* <button className={styles.footerNext} onClick={onNext} disabled={disabled}>
        {nextLabel} <ChevronRight />
      </button> */}
      <BtnContinue  onClick={onNext} disabled={disabled}/>
    </div>
  );
}

// ── Sidebar ──────────────────────────────────────────────────
export function Sidebar({ steps, currentStep }) {
  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarCard}>
        <h3 className={styles.sidebarTitle}>Your Progress</h3>
        <div className={styles.sidebarSteps}>
          {steps.map((s, idx) => {
            const done = idx < currentStep;
            const active = idx === currentStep;
            return (
              <div
                key={s.id}
                className={`${styles.sidebarStep} ${done ? styles.sidebarStepDone : ''} ${active ? styles.sidebarStepActive : ''}`}
              >
                <div className={styles.sidebarBubble}>
                  {done ? <Check /> : <span>{idx + 1}</span>}
                </div>
                <span className={styles.sidebarLabel}>{s.label}</span>
              </div>
            );
          })}
        </div>
        <div className={styles.sidebarPrivacy}>
          <span>🔒</span>
          <span>Private &amp; confidential</span>
        </div>
      </div>
    </aside>
  );
}

// ══════════════════════════════════════════════════════════
// REUSABLE QUESTION INPUTS
// ══════════════════════════════════════════════════════════

// ── Toggle (single-select chips) ────────────────────────────
export function ToggleGroup({ options, value, onChange }) {
  return (
    <div className={styles.toggleGroup}>
      {options.map((opt) => {
        const label = typeof opt === 'object' ? opt.label : opt;
        const sel = value === label;
        return (
          <button
            key={label}
            className={`${styles.toggleBtn} ${sel ? styles.toggleBtnActive : ''}`}
            onClick={() => onChange(label)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Multi-chip select ────────────────────────────────────────
export function MultiChip({ options, selected = [], onChange, noneValue }) {
  const toggle = (label) => {
    if (label === noneValue) { onChange([noneValue]); return; }
    const filtered = selected.filter((x) => x !== noneValue);
    if (filtered.includes(label)) onChange(filtered.filter((x) => x !== label));
    else onChange([...filtered, label]);
  };
  return (
    <div className={styles.chipGrid}>
      {options.map((opt) => {
        const label = typeof opt === 'object' ? opt.label : opt;
        const sel = selected.includes(label);
        return (
          <button
            key={label}
            className={`${styles.chipBtn} ${sel ? styles.chipBtnActive : ''}`}
            onClick={() => toggle(label)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Option Rows (radio-style full-width) ─────────────────────
export function OptionList({ options, value, onChange }) {
  return (
    <div className={styles.optionList}>
      {options.map((opt) => {
        const label = typeof opt === 'object' ? opt.label : opt;
        const sub = typeof opt === 'object' ? opt.sub : undefined;
        const icon = typeof opt === 'object' ? opt.icon : undefined;
        const sel = value === label;
        return (
          <button
            key={label}
            className={`${styles.optionRow} ${sel ? styles.optionRowActive : ''}`}
            onClick={() => onChange(label)}
          >
            {icon && <span className={styles.optionIcon}>{icon}</span>}
            <div className={styles.optionTexts}>
              <span className={styles.optionLabel}>{label}</span>
              {sub && <span className={styles.optionSub}>{sub}</span>}
            </div>
            {sel && <span className={styles.optionCheckmark}><Check /></span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Concern icon-chip grid ───────────────────────────────────
export function ConcernGrid({ options, selected = [], onChange }) {
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((c) => c !== id));
    else onChange([...selected, id]);
  };
  return (
    <div className={styles.concernGrid}>
      {options.map((opt) => {
        const sel = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            className={`${styles.concernChip} ${sel ? styles.concernChipActive : ''}`}
            onClick={() => toggle(opt.id)}
          >
            <span className={styles.concernChipIcon}>{opt.icon}</span>
            <span>{opt.label}</span>
            {sel && <span className={styles.concernCheckBadge}><Check size={8} /></span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Emoji grid ───────────────────────────────────────────────
export function EmojiGrid({ options, value, onChange, cols = 5 }) {
  return (
    <div className={styles.emojiGrid} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {options.map((opt) => {
        const label = typeof opt === 'object' ? opt.label : opt;
        const emoji = typeof opt === 'object' ? opt.emoji : opt;
        const sel = value === label;
        return (
          <button
            key={label}
            className={`${styles.emojiCard} ${sel ? styles.emojiCardActive : ''}`}
            onClick={() => onChange(label)}
          >
            <span className={styles.emojiCardBigIcon}>{emoji}</span>
            <span className={styles.emojiCardLabel}>{label}</span>
            {sel && <span className={styles.emojiCardCheck}><Check size={8} /></span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Stress rater 1-5 ─────────────────────────────────────────
export function StressRater({ value, onChange }) {
  const labels = ['Very\nlow', 'Low', 'Moderate', 'High', 'Very\nhigh'];
  return (
    <div className={styles.stressRater}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          className={`${styles.stressBtn} ${value === n ? styles.stressBtnActive : ''}`}
          onClick={() => onChange(n)}
        >
          <span className={styles.stressNum}>{n}</span>
          <span className={styles.stressLbl}>{labels[n - 1]}</span>
        </button>
      ))}
    </div>
  );
}

// ── Yes/No row ───────────────────────────────────────────────
export function YesNo({ value, onChange, options = ['No', 'Yes'] }) {
  return (
    <div className={styles.yesNoRow}>
      {options.map((opt) => (
        <button
          key={opt}
          className={`${styles.yesNoBtn} ${value === opt ? styles.yesNoBtnActive : ''}`}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// ── Slider ───────────────────────────────────────────────────
export function Slider({ value, onChange, min = 0, max = 10, step = 1, unit, leftLabel = 'Low', rightLabel = 'High' }) {
  const pct = ((value - min) / (max - min)) * 100;
  const display = unit ? `${value} ${unit}` : value;
  return (
    <div className={styles.sliderWrap}>
      <div className={styles.sliderRow}>
        <span className={styles.sliderEndLabel}>{leftLabel}</span>
        <div className={styles.sliderTrack} style={{ position: 'relative', flex: 1 }}>
          <span className={styles.sliderVal} style={{ left: `${pct}%` }}>{display}</span>
          <input
            type="range" className={styles.slider}
            min={min} max={max} step={step} value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ background: `linear-gradient(to right, var(--sc-accent,#6D28D9) ${pct}%, var(--sc-accent-200,#DDD6FE) ${pct}%)` }}
          />
        </div>
        <span className={styles.sliderEndLabel}>{rightLabel}</span>
      </div>
    </div>
  );
}

// ── Medical conditions list (condition + Yes/No inline) ───────
export function MedicalList({ conditions, answers, onChange }) {
  return (
    <div className={styles.medicalList}>
      {conditions.map((cond) => (
        <div key={cond.id} className={styles.medicalRow}>
          <span className={styles.medicalLabel}>{cond.label}</span>
          <div className={styles.medicalYesNo}>
            {['No', 'Yes'].map((opt) => (
              <button
                key={opt}
                className={`${styles.medYesNoBtn} ${answers[cond.id] === opt ? styles.medYesNoBtnActive : ''}`}
                onClick={() => onChange(cond.id, opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Number input ─────────────────────────────────────────────
export function NumberInput({ value, onChange, placeholder = '', unit }) {
  return (
    <div className={styles.numberInputWrap}>
      <input
        type="number"
        className={styles.numberInput}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {unit && <span className={styles.numberInputUnit}>{unit}</span>}
    </div>
  );
}

// ── Text input ───────────────────────────────────────────────
export function TextInput({ value, onChange, placeholder = '' }) {
  return (
    <input
      type="text"
      className={styles.textInput}
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ── Info/Warning boxes ───────────────────────────────────────
export function InfoBox({ children }) {
  return <div className={styles.infoBox}><span>ℹ️</span><span>{children}</span></div>;
}
export function WarningBox({ children }) {
  return <div className={styles.warningBox}><span>⚠️</span><span>{children}</span></div>;
}
export function CrisisAlert({ children }) {
  return <div className={styles.crisisAlert}><span>📞</span><span>{children}</span></div>;
}
export function RedFlagBanner() {
  return (
    <div className={styles.redFlagBanner}>
      <span>🚨</span>
      <span className={styles.redFlagText}>
        <strong>This may require urgent medical attention.</strong> Please contact a doctor or visit a hospital immediately.
      </span>
    </div>
  );
}

// ── Review row ───────────────────────────────────────────────
export function ReviewRow({ icon, label, value, onEdit }) {
  return (
    <div className={styles.reviewRow}>
      {icon && <span className={styles.reviewRowIcon}>{icon}</span>}
      <div className={styles.reviewRowTexts}>
        <span className={styles.reviewRowLabel}>{label}</span>
        <span className={styles.reviewRowValue}>{value || '—'}</span>
      </div>
      {onEdit && (
        <button className={styles.reviewEditBtn} onClick={onEdit} aria-label={`Edit ${label}`}>
          <EditIcon />
        </button>
      )}
    </div>
  );
}

// ── Gauge (semicircle result meter) ──────────────────────────
export function ResultGauge({ pct, riskLevel, riskColor, riskBg, riskBorder, riskEmoji, riskDesc }) {
  return (
    <div className={styles.gaugeCard} style={{ background: riskBg, borderColor: riskBorder }}>
      <div className={styles.gaugeLeft}>
        <span className={styles.gaugeRiskEmoji}>{riskEmoji}</span>
        <h3 className={styles.gaugeRiskLevel} style={{ color: riskColor }}>{riskLevel}</h3>
        <p className={styles.gaugeRiskDesc}>{riskDesc}</p>
      </div>
      <div className={styles.gaugeRight}>
        <svg viewBox="0 0 120 70" className={styles.gaugeSvg}>
          <defs>
            <linearGradient id="gGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <path d="M10 65 A50 50 0 0 1 110 65" stroke="#e5e7eb" strokeWidth="12" fill="none" strokeLinecap="round" />
          <path
            d="M10 65 A50 50 0 0 1 110 65"
            stroke="url(#gGrad)" strokeWidth="12" fill="none" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 157} 157`}
          />
          <g transform={`rotate(${-90 + (pct / 100) * 180} 60 65)`}>
            <line x1="60" y1="65" x2="60" y2="22" stroke="#1e1b4b" strokeWidth="2" strokeLinecap="round" />
            <circle cx="60" cy="65" r="4" fill="#1e1b4b" />
          </g>
        </svg>
      </div>
    </div>
  );
}

// ── Confidence bar ───────────────────────────────────────────
export function ConfidenceBar({ pct, color }) {
  return (
    <div className={styles.confidenceRow}>
      <span className={styles.confidenceLabel}>Confidence in result</span>
      <span className={styles.confidencePctLabel} style={{ color }}>{pct}%</span>
      <div className={styles.confidenceTrack}>
        <div className={styles.confidenceFill} style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ── Next-action list ─────────────────────────────────────────
export function NextActionList({ actions }) {
  return (
    <div className={styles.nextActionList}>
      {actions.map((a) => (
        <button key={a.title} className={styles.nextActionRow}>
          <span className={styles.nextActionIcon}>{a.icon}</span>
          <div className={styles.nextActionTexts}>
            <span className={styles.nextActionTitle}>{a.title}</span>
            <span className={styles.nextActionDesc}>{a.desc}</span>
          </div>
          <ChevronRight />
        </button>
      ))}
    </div>
  );
}
